import { useCallback } from "react";
import type { MouseEvent } from "react";
import { useQueryState } from "nuqs";
import { useNavigate, useParams } from "react-router-dom";
import { IPlanningViewsGroup, PlanningViewBy } from "../types";

const OPENABLE_VIEWS: PlanningViewBy[] = [
  PlanningViewBy.EPICS,
  PlanningViewBy.STORIES,
];

export const usePlanningDetail = () => {
  const [, setTid] = useQueryState("tid");
  const navigate = useNavigate();
  const { project, team } = useParams<{ project: string; team: string }>();

  const openTicketDetail = useCallback(
    (ticketId?: string) => {
      if (!ticketId) return;
      setTid(ticketId);
    },
    [setTid],
  );

  const canOpenPlanningDetail = useCallback(
    (viewBy: PlanningViewBy, group: IPlanningViewsGroup) =>
      OPENABLE_VIEWS.includes(viewBy) &&
      !group.isUnassigned &&
      Boolean(group.id),
    [],
  );

  const canOpenSprintDetail = useCallback(
    (viewBy: PlanningViewBy, group: IPlanningViewsGroup) =>
      viewBy === PlanningViewBy.SPRINTS &&
      !group.isUnassigned &&
      Boolean(group.id),
    [],
  );

  const openSprintDetail = useCallback(
    (sprintId?: string) => {
      if (!sprintId || !project || !team) return;

      navigate(`/app/${project}/${team}/sprints`, { state: { sprintId } });
    },
    [navigate, project, team],
  );

  const handlePlanningTileNameClick = useCallback(
    (
      viewBy: PlanningViewBy,
      group: IPlanningViewsGroup,
      event?: MouseEvent<HTMLElement>,
    ) => {
      event?.stopPropagation();
      if (canOpenPlanningDetail(viewBy, group)) {
        openTicketDetail(group.id);
        return;
      }

      if (canOpenSprintDetail(viewBy, group)) {
        openSprintDetail(group.id);
      }
    },
    [
      canOpenPlanningDetail,
      canOpenSprintDetail,
      openTicketDetail,
      openSprintDetail,
    ],
  );

  return {
    openTicketDetail,
    canOpenPlanningDetail,
    canOpenSprintDetail,
    handlePlanningTileNameClick,
  };
};
